import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import ProtectedRoute from './ProtectedRoute';
import Header from './Header';
import Sidebar from './Sidebar';
import { useAuth } from '../context/AuthContext';
import Dashboard from '../pages/Dashboard';
import Projects from '../pages/Projects';
import Environments from '../pages/Environments';
import Swagger from '../pages/Swagger';
import RawJsonTable from '../pages/RawJsonTable';
import TestCycles from '../pages/TestCycles';
import EnvVariables from '../pages/EnvVariables';
import Functions from '../pages/Functions';
import ManageAccount from '../pages/ManageAccount';
import Pricing from '../pages/Pricing';
import Payment from '../pages/Payment';
import PaymentSuccess from '../pages/PaymentSuccess';
import PaymentCancel from '../pages/PaymentCancel';
import LicensePaymentSuccess from '../pages/LicensePaymentSuccess';
import ApiExecutionAddonPaymentSuccess from '../pages/ApiExecutionAddonPaymentSuccess';
import TenantAddonPaymentSuccess from '../pages/TenantAddonPaymentSuccess';
import Profile from '../pages/Profile';
import SidebarHistory from '../pages/SidebarHistory';
import Console from '../pages/Console';
import Trash from '../pages/Trash';
import Help from '../pages/Help';
import ProxySettings from '../pages/ProxySettings';
import SuperAdminDashboard from '../pages/SuperAdminDashboard';
import AdminLoginHistoryPage from '../pages/AdminLoginHistoryPage';
import AdminUserActivityPage from '../pages/AdminUserActivityPage';
import AdminUserDetailPage from '../pages/AdminUserDetailPage';
import SuperAdminAuditLogsPage from '../pages/SuperAdminAuditLogsPage';
import SuperAdminSecurityControlsPage from '../pages/SuperAdminSecurityControlsPage';
import SuperAdminDataReportsPage from '../pages/SuperAdminDataReportsPage';
import SuperAdminSystemConfigurationPage from '../pages/SuperAdminSystemConfigurationPage';
import SuperAdminAdvancedMonitoringPage from '../pages/SuperAdminAdvancedMonitoringPage';
import SuperAdminAlertsPage from '../pages/SuperAdminAlertsPage';
import SuperAdminRbacPage from '../pages/SuperAdminRbacPage';
import SuperAdminUserManagementPage from '../pages/SuperAdminUserManagementPage';
import SuperAdminTenantManagementPage from '../pages/SuperAdminTenantManagementPage';
import SuperAdminGrievancePage from '../pages/SuperAdminGrievancePage';
import SuperAdminRevenuePage from '../pages/SuperAdminRevenuePage';
import Login from '../pages/Login';
import Register from '../pages/Register';
import ForgotPassword from '../pages/ForgotPassword';
import AdminProtectedRoute from './AdminProtectedRoute';
import { useTenant } from '../context/TenantContext';
import TenantNotFound from '../pages/TenantNotFound';
import HelpChatWidget from './HelpChatWidget';
import TestCycleExecutionBanner from './TestCycleExecutionBanner';
import { useTestCycleExecution } from '../context/TestCycleExecutionContext';
import CollectionExecutionBanner from './CollectionExecutionBanner';
import BillingWarningBanner from './BillingWarningBanner';
import { isSuperAdminUser, SUPER_ADMIN_ACTIVITY_HOME } from '../utils/superAdmin';
import SuperAdminProjectManagementPage from '../pages/SuperAdminProjectManagementPage';
import SuperAdminEnvironmentManagementPage from '../pages/SuperAdminEnvironmentManagementPage';
import SuperAdminPlanManagementPage from '../pages/SuperAdminPlanManagementPage';
import { ProxyConnectionProvider } from '../context/ProxyConnectionContext';
import ProxyRequiredRoute from './ProxyRequiredRoute';
import PostRegistrationWalkthrough from './PostRegistrationWalkthrough';
import NewUserFeatureTour from './NewUserFeatureTour';
import { SubscriptionGuardProvider } from '../context/SubscriptionGuardContext';
import { useProxyConnection } from '../context/ProxyConnectionContext';
import {
  DFT_BRAND,
  HEADER_HEIGHT_PX,
  APP_PAGE_ZOOM_VAR,
  APP_HEADER_LAYOUT_HEIGHT,
  applyAppViewportCssVars,
  resolveAppPageZoom,
} from '../styles/brandPalette';

const AIAgent = lazy(() =>
  import('../pages/AIAgent').then((m) => ({ default: m.AIAgentPage }))
);

const Collections = lazy(() => import('../pages/Collections'));

/** Lazy: TestCycleDetailsPage eagerly pulls Collections (~40k lines) for named helpers; loading it
 * with TenantDomainRoutes caused webpack TDZ on default exports (TenantAppLayout crash). */
const TestCycleDetailsPage = lazy(() => import('../pages/TestCycleDetailsPage'));

const AdminOnlyManageAccountGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const isManageAccountPath = pathname === '/manage-account' || pathname.startsWith('/manage-account');
  const isProfilePath = pathname === '/profile' || pathname.startsWith('/profile/');
  const isAdminBillingPath =
    pathname === '/pricing' ||
    pathname.startsWith('/pricing/') ||
    pathname === '/payment' ||
    pathname.startsWith('/payment/');
  if (isSuperAdminUser(user)) {
    return <>{children}</>;
  }
  if (isLoading) {
    return <>{children}</>;
  }
  if (user?.role === 'ADMIN' && !isManageAccountPath && !isAdminBillingPath && !isProfilePath) {
    return (
      <Navigate
        to={{ pathname: '/manage-account', search: location.search }}
        replace
      />
    );
  }
  return <>{children}</>;
};

/** Legacy `/test-cycles` URLs → `/test-plans` (same query string). */
const LegacyTestCyclesRedirect: React.FC = () => {
  const { cycleId } = useParams();
  const location = useLocation();
  const target = cycleId
    ? `/test-plans/${cycleId}${location.search}`
    : `/test-plans${location.search}`;
  return <Navigate to={target} replace />;
};

/**
 * Routes for tenant subdomain ({org}.crudexpert.com)
 * Shows application with tenant context
 */
const CONSOLE_HEIGHT_MIN_PX = 120;
const CONSOLE_HEIGHT_MAX_VH = 85;
const TENANT_PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/payment/success',
  '/payment/cancel',
  '/payment/license-success',
  '/payment/api-execution-addon-success',
  '/payment/tenant-addon-success',
]);
const CONSOLE_HEIGHT_DEFAULT_PX = Math.round((window.innerHeight - 64) * 0.5);

/**
 * Authenticated tenant shell: super-admin users only see activity / audit pages.
 */
function testPlanCycleIdFromPath(pathname: string): number | null {
  const match = pathname.match(/^\/test-plans\/(\d+)/);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

const TenantAppLayout: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { activeExecution } = useTestCycleExecution();
  // The run loop lives on the test plan page. Keep that same instance mounted
  // while it is executing, or opening Collections (or any other page) unmounts
  // it and the run stays at 0 completed.
  const savedTestPlanLocationRef = React.useRef<typeof location | null>(null);
  const viewingTestPlanDetails = testPlanCycleIdFromPath(location.pathname) != null;
  if (viewingTestPlanDetails) {
    savedTestPlanLocationRef.current = location;
  }
  const savedTestPlanLocation = savedTestPlanLocationRef.current;
  const savedTestPlanCycleId = savedTestPlanLocation
    ? testPlanCycleIdFromPath(savedTestPlanLocation.pathname)
    : null;
  const keepTestPlanMounted =
    activeExecution?.executing === true &&
    savedTestPlanCycleId != null &&
    savedTestPlanCycleId === activeExecution.cycleId;
  const showTestPlanDetails = viewingTestPlanDetails || keepTestPlanMounted;
  const testPlanLocation = viewingTestPlanDetails ? location : savedTestPlanLocation;
  const { handleProxyProtectedNavigation } = useProxyConnection();
  const isSuperAdmin = isSuperAdminUser(user);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  const { isTenantDomain, isTenantValid } = useTenant();
  const isCollectionsRoute = location.pathname.startsWith('/collections');
  const isDashboardRoute =
    location.pathname === '/' ||
    location.pathname === '/dashboard' ||
    location.pathname.startsWith('/dashboard/');
  const isAiAgentRoute =
    location.pathname === '/ai-agent' || location.pathname.endsWith('/ai-agent');
  const isProjectsRoute =
    location.pathname === '/projects' || location.pathname.startsWith('/projects/');
  const isEnvironmentsRoute =
    location.pathname === '/environments' || location.pathname.startsWith('/environments/');
  const isSwaggerRoute =
    location.pathname === '/swagger' || location.pathname.startsWith('/swagger/');
  const isTestPlansRoute =
    location.pathname === '/test-plans' || location.pathname.startsWith('/test-plans/');
  const isEnvVariablesRoute =
    location.pathname === '/env-variables' || location.pathname.startsWith('/env-variables/');
  const isFunctionsRoute =
    location.pathname === '/functions' || location.pathname.startsWith('/functions/');
  const isProxySettingsRoute =
    location.pathname === '/proxy-settings' || location.pathname.startsWith('/proxy-settings/');
  const isHelpRoute =
    location.pathname === '/help' || location.pathname.startsWith('/help/');
  const isWorkspaceChromeRoute =
    isCollectionsRoute ||
    isDashboardRoute ||
    isAiAgentRoute ||
    isProjectsRoute ||
    isEnvironmentsRoute ||
    isSwaggerRoute ||
    isTestPlansRoute ||
    isEnvVariablesRoute ||
    isFunctionsRoute ||
    isProxySettingsRoute ||
    isHelpRoute;
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [isConsoleOpen, setIsConsoleOpen] = React.useState(false);
  const [consoleHeightPx, setConsoleHeightPx] = React.useState(CONSOLE_HEIGHT_DEFAULT_PX);
  const mainColumnRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef({ isDragging: false, startY: 0, startHeight: 0 });

  /** ⌘+Option+C (Mac) / Ctrl+Alt+C (Win/Linux): open proxy console panel */
  React.useEffect(() => {
    if (!isTenantDomain || isTenantValid !== true) return;
    if (TENANT_PUBLIC_PATHS.has(location.pathname)) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey || (!e.metaKey && !e.ctrlKey)) return;
      if (e.code !== 'KeyC' || e.repeat) return;
      const el = e.target as HTMLElement | null;
      if (!el) return;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return;
      e.preventDefault();
      handleProxyProtectedNavigation('/console', () => setIsConsoleOpen(true));
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isTenantDomain, isTenantValid, location.pathname, handleProxyProtectedNavigation]);

  React.useEffect(() => {
    if (isMdUp) setMobileNavOpen(false);
  }, [isMdUp]);

  React.useLayoutEffect(() => {
    const apply = () => applyAppViewportCssVars(Boolean(isMdUp && sidebarOpen));
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, [isMdUp, sidebarOpen]);

  React.useEffect(() => {
    return () => {
      // Keep approximate zoom for next shell mount (do not reset to CSS default 1).
      applyAppViewportCssVars(true);
    };
  }, []);

  const handleToggleSidebar = React.useCallback(() => {
    if (isMdUp) setSidebarOpen((prev) => !prev);
    else setMobileNavOpen((prev) => !prev);
  }, [isMdUp]);

  React.useEffect(() => {
    if (!isConsoleOpen || isSuperAdmin) return;
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging) return;
      const dy = dragRef.current.startY - e.clientY;
      const next = dragRef.current.startHeight + dy;
      const headerPx =
        HEADER_HEIGHT_PX *
        resolveAppPageZoom(window.innerWidth, window.innerHeight);
      const maxPx = (window.innerHeight - headerPx) * (CONSOLE_HEIGHT_MAX_VH / 100);
      const clamped = Math.round(Math.min(Math.max(next, CONSOLE_HEIGHT_MIN_PX), maxPx));
      setConsoleHeightPx(clamped);
    };
    const onMouseUp = () => {
      dragRef.current.isDragging = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isConsoleOpen, isSuperAdmin]);

  const handleConsoleResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { isDragging: true, startY: e.clientY, startHeight: consoleHeightPx };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [consoleHeightPx]);

  const mainColumnSx = {
    flex: '1 1 0%',
    minWidth: 0,
    backgroundColor: DFT_BRAND.bg,
    minHeight: 0,
    height: `calc(100vh - ${APP_HEADER_LAYOUT_HEIGHT})`,
    maxHeight: `calc(100vh - ${APP_HEADER_LAYOUT_HEIGHT})`,
    display: 'flex',
    flexDirection: 'column',
    px: isWorkspaceChromeRoute ? { xs: 0.5, sm: 0.75 } : { xs: 1.5, sm: 2.5 },
    pl: isWorkspaceChromeRoute ? { xs: 0, sm: 0.25 } : undefined,
    py: isWorkspaceChromeRoute ? { xs: 1, sm: 1 } : { xs: 1.5, sm: 2 },
    // Clip here so CSS zoom cannot create a second window-edge scrollbar on Windows.
    overflow: 'hidden',
    ...(isConsoleOpen
      ? {
          pb: { xs: 1, sm: 1.25 },
        }
      : {}),
  } as const;

  const shellSx = {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    backgroundColor: DFT_BRAND.bg,
  } as const;

  const sidebarSharedProps = {
    open: sidebarOpen,
    onClose: () => (isMdUp ? setSidebarOpen(false) : setMobileNavOpen(false)),
    onToggleOpen: () => setSidebarOpen((prev) => !prev),
    isMobile: !isMdUp,
    mobileOpen: mobileNavOpen,
    onMobileClose: () => setMobileNavOpen(false),
  } as const;

  if (isSuperAdmin) {
    return (
      <Box sx={shellSx}>
        <Sidebar
          {...sidebarSharedProps}
          onConsoleToggle={() => setIsConsoleOpen(false)}
          isConsoleOpen={false}
          superAdminMode
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header
            superAdminMode
            sidebarOpen={isMdUp ? sidebarOpen : mobileNavOpen}
            onToggleSidebar={handleToggleSidebar}
          />
          <Box ref={mainColumnRef} component="main" sx={mainColumnSx}>
          <AdminOnlyManageAccountGuard>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                zoom: { xs: 1, md: APP_PAGE_ZOOM_VAR },
              }}
            >
              <Routes>
                <Route path="/" element={<Navigate to={SUPER_ADMIN_ACTIVITY_HOME} replace />} />
                <Route path="/dashboard" element={<Navigate to={SUPER_ADMIN_ACTIVITY_HOME} replace />} />
                <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
                <Route path="/super-admin/activity-logs" element={<AdminUserActivityPage />} />
                <Route path="/super-admin/login-history" element={<AdminLoginHistoryPage />} />
                <Route path="/super-admin/audit-logs" element={<SuperAdminAuditLogsPage />} />
                <Route path="/super-admin/security-controls" element={<SuperAdminSecurityControlsPage />} />
                <Route path="/super-admin/user-details" element={<AdminUserDetailPage />} />
                <Route path="/super-admin/project-management" element={<SuperAdminProjectManagementPage />} />
                <Route path="/super-admin/environment-management" element={<SuperAdminEnvironmentManagementPage />} />
                <Route path="/super-admin/user-management" element={<SuperAdminUserManagementPage />} />
                <Route path="/super-admin/tenant-management" element={<SuperAdminTenantManagementPage />} />
                <Route path="/super-admin/plan-management" element={<SuperAdminPlanManagementPage />} />
                <Route path="/super-admin/grievance" element={<SuperAdminGrievancePage />} />
                <Route path="/super-admin/revenue" element={<SuperAdminRevenuePage />} />
                <Route path="/super-admin/rbac" element={<SuperAdminRbacPage />} />
                <Route path="/super-admin/alerts" element={<SuperAdminAlertsPage />} />
                <Route path="/super-admin/advanced-monitoring" element={<SuperAdminAdvancedMonitoringPage />} />
                <Route path="/super-admin/system-configuration" element={<SuperAdminSystemConfigurationPage />} />
                <Route path="/super-admin/data-reports" element={<SuperAdminDataReportsPage />} />
                <Route path="*" element={<Navigate to={SUPER_ADMIN_ACTIVITY_HOME} replace />} />
              </Routes>
            </Box>
          </AdminOnlyManageAccountGuard>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      <Sidebar
        {...sidebarSharedProps}
        onConsoleToggle={() => {
          handleProxyProtectedNavigation('/console', () => setIsConsoleOpen((prev) => !prev));
        }}
        isConsoleOpen={isConsoleOpen}
      />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Header
          sidebarOpen={isMdUp ? sidebarOpen : mobileNavOpen}
          onToggleSidebar={handleToggleSidebar}
        />
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
        <Box ref={mainColumnRef} component="main" sx={mainColumnSx}>
        <TestCycleExecutionBanner />
        <CollectionExecutionBanner />
        <BillingWarningBanner />
        <AdminOnlyManageAccountGuard>
          <Box
            className={isCollectionsRoute ? 'collections-chrome' : undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              zoom: { xs: 1, md: APP_PAGE_ZOOM_VAR },
              ...(isAiAgentRoute
                ? {
                    // Fixed two-pane chat shell — forms/selects scroll inside instead of
                    // stretching the page (gray gap / UI drag). Heights subtract main padding.
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: isConsoleOpen ? 0 : undefined,
                    height: isConsoleOpen
                      ? '100%'
                      : {
                          xs: 'calc(100dvh - 64px - 16px)',
                          sm: 'calc(100dvh - 64px - 16px)',
                          md: `calc(100vh / ${APP_PAGE_ZOOM_VAR} - 64px - 16px)`,
                        },
                    maxHeight: isConsoleOpen
                      ? '100%'
                      : {
                          xs: 'calc(100dvh - 64px - 16px)',
                          sm: 'calc(100dvh - 64px - 16px)',
                          md: `calc(100vh / ${APP_PAGE_ZOOM_VAR} - 64px - 16px)`,
                        },
                  }
                : {
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                  }),
            }}
          >
            {showTestPlanDetails && testPlanLocation ? (
              <Box sx={{ display: viewingTestPlanDetails ? 'contents' : 'none' }}>
                <Routes location={testPlanLocation}>
                  <Route
                    path="/test-plans/:cycleId"
                    element={
                      <ProxyRequiredRoute>
                        <Suspense
                          fallback={
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                              <CircularProgress />
                            </Box>
                          }
                        >
                          <TestCycleDetailsPage />
                        </Suspense>
                      </ProxyRequiredRoute>
                    }
                  />
                </Routes>
              </Box>
            ) : null}
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/payment" element={<Payment />} />
              <Route
                path="/collections"
                element={
                  <ProxyRequiredRoute>
                    <Suspense
                      fallback={
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress />
                        </Box>
                      }
                    >
                      <Collections />
                    </Suspense>
                  </ProxyRequiredRoute>
                }
              />
              <Route path="/projects" element={<Projects />} />
              <Route path="/environments" element={<Environments />} />
              <Route path="/swagger" element={<ProxyRequiredRoute><Swagger /></ProxyRequiredRoute>} />
              <Route path="/raw-json-table" element={<ProxyRequiredRoute><RawJsonTable /></ProxyRequiredRoute>} />
              <Route path="/test-plans" element={<ProxyRequiredRoute><TestCycles /></ProxyRequiredRoute>} />
              <Route path="/test-cycles" element={<LegacyTestCyclesRedirect />} />
              <Route path="/test-cycles/:cycleId" element={<LegacyTestCyclesRedirect />} />
              <Route path="/env-variables" element={<EnvVariables />} />
              <Route path="/functions" element={<Functions />} />
              <Route path="/proxy-settings" element={<ProxySettings />} />
              <Route path="/console" element={<ProxyRequiredRoute><Console /></ProxyRequiredRoute>} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/history" element={<SidebarHistory />} />
              <Route path="/help" element={<Help />} />
              <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
              <Route path="/super-admin/login-history" element={<AdminLoginHistoryPage />} />
              <Route path="/super-admin/activity-logs" element={<AdminUserActivityPage />} />
              <Route path="/super-admin/audit-logs" element={<SuperAdminAuditLogsPage />} />
              <Route path="/super-admin/security-controls" element={<SuperAdminSecurityControlsPage />} />
              <Route path="/super-admin/user-management" element={<SuperAdminUserManagementPage />} />
              <Route path="/super-admin/tenant-management" element={<SuperAdminTenantManagementPage />} />
              <Route path="/super-admin/plan-management" element={<SuperAdminPlanManagementPage />} />
              <Route path="/super-admin/grievance" element={<SuperAdminGrievancePage />} />
              <Route path="/super-admin/revenue" element={<SuperAdminRevenuePage />} />
              <Route path="/super-admin/rbac" element={<SuperAdminRbacPage />} />
              <Route path="/super-admin/alerts" element={<SuperAdminAlertsPage />} />
              <Route path="/super-admin/advanced-monitoring" element={<SuperAdminAdvancedMonitoringPage />} />
              <Route path="/super-admin/system-configuration" element={<SuperAdminSystemConfigurationPage />} />
              <Route path="/super-admin/data-reports" element={<SuperAdminDataReportsPage />} />
              <Route path="/super-admin/user-details" element={<AdminUserDetailPage />} />
              <Route path="/trash" element={<Trash />} />
              <Route
                path="/ai-agent"
                element={
                  <ProxyRequiredRoute>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: '100%',
                        maxHeight: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }}
                    >
                      <Suspense
                        fallback={
                          <Box
                            sx={{
                              flex: 1,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              p: 4,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        }
                      >
                        <AIAgent />
                      </Suspense>
                    </Box>
                  </ProxyRequiredRoute>
                }
              />
              <Route
                path="/manage-account"
                element={
                  <AdminProtectedRoute>
                    <ManageAccount />
                  </AdminProtectedRoute>
                }
              />
            </Routes>
          </Box>
          {isConsoleOpen && (
            <Box
              sx={{
                mt: 1.5,
                flexShrink: 0,
                height: consoleHeightPx,
                minHeight: CONSOLE_HEIGHT_MIN_PX,
                borderRadius: 1,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                role="separator"
                aria-valuenow={consoleHeightPx}
                aria-valuemin={CONSOLE_HEIGHT_MIN_PX}
                aria-valuemax={800}
                aria-label="Resize console"
                tabIndex={0}
                onMouseDown={handleConsoleResizeStart}
                sx={{
                  height: 8,
                  minHeight: 8,
                  cursor: 'ns-resize',
                  flexShrink: 0,
                  bgcolor: 'action.hover',
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                  '&:focus': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 3,
                    borderRadius: 1.5,
                    bgcolor: 'text.secondary',
                    opacity: 0.6,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <Console embedded onClose={() => setIsConsoleOpen(false)} />
              </Box>
            </Box>
          )}
        </AdminOnlyManageAccountGuard>
        </Box>
        <HelpChatWidget />
        </Box>
      </Box>
    </Box>
  );
};

const TenantDomainRoutes: React.FC = () => {
  const { isTenantDomain, isTenantValid } = useTenant();

  if (!isTenantDomain) {
    return null;
  }

  if (isTenantValid === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100vw',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} size={48} />
      </Box>
    );
  }

  if (isTenantValid === false) {
    return <TenantNotFound />;
  }

  return (
    <Routes>
      {/* Auth routes - only for valid tenant */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Payment callback routes - public (Stripe redirects here without auth) */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/payment/license-success" element={<LicensePaymentSuccess />} />
      <Route path="/payment/api-execution-addon-success" element={<ApiExecutionAddonPaymentSuccess />} />
      <Route path="/payment/tenant-addon-success" element={<TenantAddonPaymentSuccess />} />

      {/* Main app routes - with header/sidebar and protected */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <SubscriptionGuardProvider>
              <ProxyConnectionProvider>
                <PostRegistrationWalkthrough />
                <NewUserFeatureTour />
                <TenantAppLayout />
              </ProxyConnectionProvider>
            </SubscriptionGuardProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default TenantDomainRoutes;
